import {HtmlHelper} from './htmlHelper';
export interface ElemOptions {
  onlyIf?: boolean;
  class?: string;
  text?: string;
  children?: (HTMLElement | null)[];
  onClick?: () => unknown;
  fn?: (node: HTMLElement) => void;
}

// TODO: maybe always return this wrapper to make the API more clean?
export class ElemWrapper {
  public readonly nativeNode: HTMLElement;

  constructor(nativeNode: HTMLElement) {
    this.nativeNode = nativeNode;
  }
}

/**
 * Creates a HTMLElement in a developer-firendly way.
 * @param tag tag name of the element.
 * @param options options to specify the element to render
 */
export function Elem(tag: string, options: ElemOptions = {}): HTMLElement {
  const node = HtmlHelper.instance.document.createElement(tag);
  if (options.class) {
    node.className = options.class;
  }

  if (options.text) {
    setText(node, options.text);
  }

  if (options.children) {
    for (const child of options.children) {
      if (child !== null) node.appendChild(child);
    }
  }

  if (options.onClick) {
    setOnClick(node, options.onClick);
  }

  if (options.fn) {
    options.fn(node);
  }

  return node;
}

export function getElement(selector: string): HTMLElement | null {
  return document.querySelector(selector);
}

export function getElements(selector: string): HTMLElement[] {
  const ret: HTMLElement[] = [];
  const nodeList = HtmlHelper.instance.document.querySelectorAll(selector);
  for (let i = 0; i < nodeList.length; i++) {
    ret.push(nodeList[i] as HTMLElement);
  }
  return ret;
}

export function clearHtmlContent(parent: HTMLElement | null): boolean {
  if (!parent) return false;
  parent.innerHTML = '';
  return true;
}

export function setOnClick(node: HTMLElement | null, fn: () => void): void {
  if (!node) return;
  node.onclick = event => {
    event.preventDefault();
    event.stopPropagation();

    fn();
  };
}

export function setOnFormSubmit(
  formNode: HTMLElement | null,
  fn: () => void,
): void {
  if (!formNode) {
    return;
  }

  formNode.onsubmit = event => {
    event.preventDefault();
    fn();
  };
}

export function setText(node: HTMLElement | null, text: string | number): void {
  if (!node) return;
  node.textContent = typeof text !== 'string' ? text.toString() : text;
  node.innerHTML = node.innerHTML.replace(/\n\r?/g, '<br />');
}

export function setHtmlContent(
  parent: HTMLElement | null,
  content: HTMLElement,
): boolean {
  if (!clearHtmlContent(parent)) return false;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  parent!.appendChild(content);
  return true;
}

export function setHtmlContentMultiple(
  parent: HTMLElement | null,
  contents: Node[],
): boolean {
  if (!clearHtmlContent(parent)) return false;
  for (const content of contents) {
    if (parent !== null && content !== null) {
      parent.appendChild(content);
    }
  }
  return true;
}

export function getInputTextValue(node: HTMLElement | null): string | null {
  if (!node) return null;
  const n = node as HTMLInputElement;
  return n.value;
}

export function getSelectValue(node: HTMLElement | null): string | null {
  if (!node) return null;
  const n = node as HTMLSelectElement;
  return n.options[n.selectedIndex].value;
}

/**
 * This way we can render elements or hide elements by
 * prefixing the property with "!".
 * `renderIf => !renderIf`;
 * `!renderIf => renderIf`
 */
export function negativeProperty(property: string): string {
  if (property.startsWith('!')) {
    return property.slice(1);
  }
  return `!${property}`;
}
